import { Check, Shield, ShieldAlert, ShieldX } from "lucide-react";
import type { SkillFinding } from "@/hooks/use-skill";
import { useUpdateFinding } from "@/hooks/use-skill-mutations";
import { cn } from "@/lib/utils";

const SEV_ICON = { high: ShieldX, medium: ShieldAlert, low: Shield } as const;
const SEV_RING = {
  high: "border-l-danger",
  medium: "border-l-warning",
  low: "border-l-info",
} as const;

export function RiskFindings({ slug, findings }: { slug: string; findings: SkillFinding[] }) {
  const update = useUpdateFinding(slug);
  const visible = findings.filter((f) => f.status !== "dismissed");
  if (visible.length === 0) return null;
  return (
    <section className="flex flex-col gap-3">
      {visible.map((f) => {
        const Icon = SEV_ICON[f.sev];
        const acked = f.status === "acknowledged";
        return (
          <div
            key={f.id}
            className={cn(
              "flex gap-3 rounded-[9px] border border-border-1 border-l-4 bg-white p-4",
              SEV_RING[f.sev],
            )}
          >
            <Icon className="mt-0.5 size-5 shrink-0 text-fg-2" />
            <div className="flex flex-1 flex-col gap-1">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-semibold text-fg-1">{f.cat}</span>
                <span className="rounded-full bg-bg-2 px-2 py-0.5 text-[10px] font-semibold uppercase text-fg-2">
                  {f.sev}
                </span>
                <span className="text-xs text-fg-3">scan: {f.method}</span>
                {acked ? (
                  <span className="text-xs font-medium text-success">acknowledged</span>
                ) : null}
              </div>
              <p className="text-sm text-fg-2">{f.text}</p>
              <div className="mt-1 flex gap-2">
                <button
                  type="button"
                  disabled={acked || update.isPending}
                  onClick={() => update.mutate({ findingId: f.id, status: "acknowledged" })}
                  className="inline-flex items-center gap-1 rounded-md border border-border-1 px-3 py-1.5 text-xs font-medium text-fg-1 hover:bg-bg-2 disabled:opacity-50"
                >
                  <Check className="size-3.5" />
                  Acknowledge
                </button>
                <button
                  type="button"
                  disabled={update.isPending}
                  onClick={() => update.mutate({ findingId: f.id, status: "dismissed" })}
                  className="rounded-md px-3 py-1.5 text-xs font-medium text-fg-3 hover:bg-bg-2"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
}
